import { invoke, subscribe } from './realtime.js';
import { refreshJsonForm } from './json-form.js';

const controllers = new WeakMap();

function ensureStaleNotice(root) {
    let notice = root.querySelector(':scope > [data-us-realtime-form-notice]');
    if (notice) return notice;
    notice = document.createElement('div');
    notice.className = 'alert alert-warning us-realtime-notice us-realtime-form-notice';
    notice.dataset.usRealtimeFormNotice = '';
    notice.hidden = true;
    notice.setAttribute('role', 'alert');
    const text = document.createElement('span');
    text.textContent = root.dataset.usRealtimeStaleLabel || globalThis.UnionStackI18n.text("Common.ClientThisRecordChangedInAnotherSession000ce5");
    const reload = document.createElement('button');
    reload.type = 'button';
    reload.className = 'btn btn-sm btn-outline-primary';
    reload.textContent = root.dataset.usRealtimeReloadLabel || globalThis.UnionStackI18n.text("Common.Reload");
    reload.addEventListener('click', () => window.location.reload());
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'btn btn-sm btn-link';
    keep.textContent = root.dataset.usRealtimeKeepEditingLabel || globalThis.UnionStackI18n.text("Common.KeepEditing");
    keep.addEventListener('click', () => { notice.hidden = true; });
    notice.append(text, reload, keep);
    root.prepend(notice);
    return notice;
}

function ensurePresence(root) {
    let value = root.querySelector(':scope > [data-us-realtime-presence]');
    if (value) return value;
    value = document.createElement('p');
    value.className = 'us-realtime-presence';
    value.dataset.usRealtimePresence = '';
    value.hidden = true;
    value.setAttribute('role', 'status');
    root.prepend(value);
    return value;
}

function ensureLeaseNotice(form, root) {
    let notice = form.querySelector(':scope > [data-us-realtime-form-lease]');
    if (notice) return notice;
    notice = document.createElement('div');
    notice.className = 'alert alert-warning us-realtime-notice us-realtime-form-lease';
    notice.dataset.usRealtimeFormLease = '';
    notice.hidden = true;
    notice.setAttribute('role', 'status');
    notice.textContent = root.dataset.usRealtimeLeaseLabel
        || globalThis.UnionStackI18n.text("Common.RealtimeLeaseHeld");
    form.prepend(notice);
    return notice;
}

function hasEditableContent(form) {
    return Boolean(form.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]'));
}

function formFingerprint(form) {
    const values = [];
    for (const control of form.elements) {
        if (!(control instanceof HTMLInputElement
            || control instanceof HTMLSelectElement
            || control instanceof HTMLTextAreaElement)
            || !control.name
            || control.disabled) continue;
        if (control instanceof HTMLInputElement && ['button', 'image', 'reset', 'submit'].includes(control.type)) continue;

        let value;
        if (control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio')) {
            value = `${control.checked}:${control.value}`;
        } else if (control instanceof HTMLInputElement && control.type === 'file') {
            value = Array.from(control.files || []).map(file => `${file.name}:${file.size}:${file.lastModified}`);
        } else if (control instanceof HTMLSelectElement && control.multiple) {
            value = Array.from(control.selectedOptions, option => option.value);
        } else {
            value = control.value;
        }
        values.push([control.name, value]);
    }
    for (const control of form.querySelectorAll('[contenteditable="true"]')) {
        values.push([control.getAttribute('name') || control.id, control.textContent]);
    }
    return JSON.stringify(values);
}

function stableScope(form, index) {
    const explicit = form.dataset.realtimeFormScope?.trim().toLowerCase();
    if (explicit && /^[a-z0-9][a-z0-9._-]{0,79}$/.test(explicit)) return explicit;
    const section = form.closest('[data-us-profile-group], [data-us-case-group], section[id], .modal[id]');
    const action = form.getAttribute('action');
    const identity = [
        form.id,
        form.getAttribute('method') || 'get',
        action || window.location.pathname,
        section?.id || section?.dataset.usProfileGroup || section?.dataset.usCaseGroup,
    ].filter(Boolean).join('|');
    const basis = form.id || action ? identity : `${identity}|${index}`;
    let hash = 2166136261;
    for (let position = 0; position < basis.length; position += 1) {
        hash ^= basis.charCodeAt(position);
        hash = Math.imul(hash, 16777619);
    }
    return `form-${(hash >>> 0).toString(16)}`;
}

function refreshUrl(form) {
    // These editors own collection identities and widget serialization. Generic
    // scalar hydration cannot safely replace their values and version tokens.
    if (form.hasAttribute('data-extra-editor')) return null;
    const configured = form.dataset.realtimeRefreshUrl || form.dataset.usRefreshUrl;
    if (configured && !configured.includes('{')) return configured;
    const method = (form.getAttribute('method') || 'get').toUpperCase();
    return method === 'PUT' || method === 'PATCH' ? form.getAttribute('action') : null;
}

function enhance(root) {
    if (controllers.has(root)) return;
    const topic = root.dataset.realtimeRecordTopic;
    if (!topic) return;

    const forms = Array.from(root.querySelectorAll('form')).filter(hasEditableContent);
    const stateByScope = new Map();
    const staleNotice = ensureStaleNotice(root);
    const presence = ensurePresence(root);
    const leasesEnabled = root.dataset.realtimeLease === 'true';

    const release = (state, clearDirty = true) => {
        if (clearDirty) state.dirty = false;
        state.acquiring = false;
        state.heldElsewhere = false;
        state.form.dataset.realtimeLocked = 'false';
        state.notice.hidden = true;
        const token = state.token;
        state.token = null;
        if (token) void invoke('ReleaseLease', { topic, scope: state.scope }, token).catch(() => {});
    };

    const applyLease = (state, lease, fromBroadcast = false) => {
        if (!lease || lease.scope !== state.scope) return;
        if (fromBroadcast && state.acquiring && !lease.ownedByCaller && !lease.available) return;
        state.acquiring = false;
        if (lease.ownedByCaller && lease.renewalToken) {
            state.token = lease.renewalToken;
            state.heldElsewhere = false;
        } else if (lease.available) {
            state.token = null;
            state.heldElsewhere = false;
        } else {
            state.token = null;
            state.heldElsewhere = true;
        }
        state.form.dataset.realtimeLocked = String(state.heldElsewhere);
        state.notice.hidden = !state.heldElsewhere;
    };

    const acquire = state => {
        if (!leasesEnabled || state.token || state.acquiring || !state.dirty) return;
        state.acquiring = true;
        void invoke('AcquireLease', { topic, scope: state.scope })
            .then(lease => applyLease(state, lease))
            .catch(() => { state.acquiring = false; });
    };

    forms.forEach((form, index) => {
        const scope = stableScope(form, index);
        form.dataset.realtimeFormScope = scope;
        const state = {
            form,
            scope,
            notice: ensureLeaseNotice(form, root),
            dirty: false,
            acquiring: false,
            heldElsewhere: false,
            token: null,
            refreshing: false,
            refreshRequested: false,
            refreshUrl: refreshUrl(form),
            cleanFingerprint: null,
            userInteractingUntil: 0,
            initializingUntil: performance.now() + 1000,
        };
        state.cleanFingerprint = formFingerprint(form);
        stateByScope.set(scope, state);

        const markDirty = event => {
            if (root.dataset.realtimeSaving === 'true' || state.refreshing) return;
            const now = performance.now();
            const userOriginated = event.isTrusted
                || now <= state.userInteractingUntil
                || now > state.initializingUntil;
            if (!userOriginated) {
                state.cleanFingerprint = formFingerprint(form);
                return;
            }
            state.dirty = formFingerprint(form) !== state.cleanFingerprint;
            if (state.dirty) acquire(state);
            else release(state);
        };
        const markUserInteraction = () => { state.userInteractingUntil = performance.now() + 1500; };
        form.addEventListener('pointerdown', markUserInteraction);
        form.addEventListener('keydown', markUserInteraction);
        form.addEventListener('focusin', markUserInteraction);
        form.addEventListener('input', markDirty);
        form.addEventListener('change', markDirty);
        form.addEventListener('submit', () => { root.dataset.realtimeSaving = 'true'; });
        form.addEventListener('reset', () => window.setTimeout(() => {
            state.cleanFingerprint = formFingerprint(form);
            release(state);
        }));
        form.addEventListener('unionstack:form:success', () => {
            root.dataset.realtimeSaving = 'false';
            state.cleanFingerprint = formFingerprint(form);
            release(state);
        });
        form.addEventListener('unionstack:form:refreshed', () => {
            state.cleanFingerprint = formFingerprint(form);
            release(state);
        });
        form.addEventListener('unionstack:form:error', () => { root.dataset.realtimeSaving = 'false'; });
    });

    const releaseAll = clearDirty => stateByScope.forEach(state => release(state, clearDirty));

    const refreshCleanForm = async state => {
        if (!state.refreshUrl || state.dirty) return false;
        if (state.refreshing) {
            state.refreshRequested = true;
            return true;
        }

        state.refreshing = true;
        try {
            do {
                state.refreshRequested = false;
                await refreshJsonForm(state.form, state.refreshUrl);
            } while (state.refreshRequested && !state.dirty);
            return true;
        } catch {
            return false;
        } finally {
            state.refreshing = false;
        }
    };

    const handleResourceChanged = async () => {
        let stale = root.dataset.realtimeSaving === 'true';
        let refreshed = false;
        const refreshes = [];

        for (const state of stateByScope.values()) {
            if (state.dirty || root.dataset.realtimeSaving === 'true') {
                release(state, false);
                stale = true;
                continue;
            }
            if (!state.refreshUrl) {
                if (state.form.hasAttribute('data-extra-editor')) stale = true;
                continue;
            }
            refreshes.push(refreshCleanForm(state).then(success => {
                refreshed ||= success;
                stale ||= !success;
            }));
        }

        await Promise.all(refreshes);
        root.dataset.realtimeStale = String(stale);
        staleNotice.hidden = !stale;
        if (refreshed) {
            window.UnionStack?.ui?.announce(
                root.dataset.usRealtimeFormUpdatedLabel || 'This form was updated from another session.',
                'polite',
            );
        } else if (!stale && root.dataset.realtimeAutoRefresh === 'true') {
            window.location.reload();
        }
    };

    const unsubscribe = subscribe(topic, (type, detail) => {
        if (type === 'resource-changed') {
            void handleResourceChanged();
        } else if (type === 'reconnected') {
            stateByScope.forEach(state => {
                state.token = null;
                state.acquiring = false;
                acquire(state);
            });
            root.dataset.realtimeStale = 'true';
            staleNotice.hidden = false;
        } else if (type === 'presence-changed') {
            const others = Math.max(0, Number(detail.viewerCount || 0) - 1);
            presence.hidden = others === 0;
            presence.textContent = others === 1
                ? (root.dataset.usRealtimePresenceOneLabel || globalThis.UnionStackI18n.text("Common.RealtimePresenceOne"))
                : (root.dataset.usRealtimePresenceManyLabel || `${others} other users are viewing this record.`).replace('{0}', String(others));
        } else if (type === 'lease-changed') {
            const state = stateByScope.get(detail.scope);
            if (state) {
                applyLease(state, detail, true);
                if (detail.available && state.dirty) acquire(state);
            }
        }
    });

    const leaseTimer = window.setInterval(() => {
        for (const state of stateByScope.values()) {
            if (!state.token) continue;
            void invoke('RenewLease', { topic, scope: state.scope }, state.token)
                .then(lease => {
                    applyLease(state, lease);
                    if (lease.available && state.dirty) acquire(state);
                })
                .catch(() => {});
        }
    }, 30000);

    window.addEventListener('pagehide', () => releaseAll(true), { once: true });
    controllers.set(root, { unsubscribe, releaseAll, stateByScope, leaseTimer });
}

function enhanceAll(root = document) {
    if (root instanceof Element && root.matches('[data-realtime-record-topic]')) enhance(root);
    root.querySelectorAll('[data-realtime-record-topic]').forEach(enhance);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhanceAll(), { once: true });
else enhanceAll();

export { enhance as enhanceRealtimeForm, enhanceAll as enhanceRealtimeForms };
